import React, { Component } from 'react';
import { RouteComponentProps } from '~utils/withRouter';
import i18next from 'i18next';
import ReactHtmlParser from 'react-html-parser';

import { withSpinner } from '~components/Spinner';
import ContentType from '~components/ContentType';
import PublicationBadge from '~components/PublicationBadge';
import { formatThousandsUSD } from '~utils/investment';

import ModalContent from './components/ModalContent';
import styles from './styles.module.scss';
import Map from './components/Map';
import { BasicProps } from './types';
import { hasFundingInfo, renderMany, renderText } from "~screens/Dashboard/screens/ProjectsIIRSA/screens/ProjectDetail/utils";
import DropDown from "~components/Dropdown";
import SingleContent
    from "~screens/Dashboard/screens/Projects/screens/ProjectDetail/screens/TechnicalSheet/components/SingleContent";
import {
    AREA,
    COUNTRIES, TERRITORIES, THEMES, SUBTHEMES,
    TYPES, STAGE, TERM, SOURCE
} from "./constants";
import Breadcrumb from "~components/Breadcrumb";
import PageHero from "~components/PageHero";
import StageProgress from "~components/StageProgress";

const names = (items?: {name: string}[]) => (items || []).map(item => item.name);
// Each name once: an Acción may list a funding type with several funders.
const distinct = (values: (string | undefined)[]) =>
    Array.from(new Set(values.filter((value): value is string => !!value)));

interface Props extends RouteComponentProps, BasicProps {
    id: number;
}

class Layout extends Component<Props, { modelOpen: boolean }> {
    constructor(props: Props) {
        super(props);

        this.state = {
            modelOpen: false
        };
    }

    handleOpenModal = () => {
        this.setState({ ...this.state, modelOpen: true });
    };

    handleCloseModal = () => {
        this.setState({ ...this.state, modelOpen: false });
    };

    SPANISH_TO_ENGLISH_INFRAESTRUCTURES = {
        ['transporte']: 'transport',
        ['energía']: 'energy',
        ['fronteras']: 'boundary',
        ['comunicaciones']: 'telecommunications',
    };

    SPANISH_TO_ENGLISH_STAGES = {
        ['perfil']: 'profiling',
        ['ejecución']: 'execution',
        ['pre-ejecución']: 'pre_execution',
        ['concluido']: 'finished',
    };

    toKey = (inf) => {
        return this.SPANISH_TO_ENGLISH_INFRAESTRUCTURES[inf.toLowerCase()];
    };

    stageToEnglish = (stage) => {
        const res = this.SPANISH_TO_ENGLISH_STAGES[stage.toLowerCase()];
        return res;
    };

    // One amount for the Acción, then the funding types and funders taking part (no amount each).
    renderFunding = () => {
        const { funding } = this.props.projectDetail;
        const funders = distinct(funding.map(fund => fund.funder?.name));
        return <>
            <SingleContent
                data={{ subtitle: i18next.t('projectDetail:dollarsInvested'), value: this.investment() }}
                className={styles.totalInvestment} />
            <SingleContent
                data={renderMany('fundingTypes', distinct(funding.map(fund => fund.fundingType?.name)))}
                valueClassName={styles.uppercase} />
            {funders.length > 0 && <SingleContent data={renderMany('funders', funders)} />}
        </>;
    };

    investment = () => {
        const thousands = formatThousandsUSD(this.props.projectDetail.totalInvestment);
        return thousands ? `$ ${thousands}` : i18next.t('projects-iirsa:unidentified');
    };

    renderDescription = () => {
        const objectives = this.props.projectDetail ? this.props.projectDetail.goal.replace('<p>', '').replace('</p>', '') : '';
        const content = this.props.projectDetail ? this.props.projectDetail.description.replace('<p>', '').replace('</p>', '') : '';
        return (
            <div className="column">
                <h3>{i18next.t('technicalSheet:objectives')}</h3>
                <p className="rich-text">{ReactHtmlParser(objectives)}</p>
                <h3>{i18next.t('technicalSheet:description')}</h3>
                <p className="rich-text">{ReactHtmlParser(content)}</p>
            </div>
        )
    };

    render() {
        const { projectDetail } = this.props;
        const { modelOpen } = this.state;

        return projectDetail && (
            <div className={styles.projectsConteiner}>
                <PageHero
                    variant="solid"
                    breadcrumb={<Breadcrumb items={[
                        { name: i18next.t('projects-iirsa:start'), url: '/home' },
                        { name: i18next.t('projectDetail:projects'), url: '/actions' },
                        { name: i18next.t('technicalSheet:projectSheet') }
                    ]} />}
                    title={<>
                        <span className={styles.titleName}>{projectDetail.name}</span>
                        <span className={styles.titleCode}>{projectDetail.code}</span>
                    </>}
                    subtitle={projectDetail.publicationStatus === 'unpublished' &&
                        <PublicationBadge status={projectDetail.publicationStatus}/>}
                    aside={(projectDetail.kml || projectDetail.kmlSimplified) &&
                        <div className={styles.minimap}>
                            {projectDetail.kml &&
                                <button type="button" className={styles.zoomMap} onClick={this.handleOpenModal}>
                                    {i18next.t('projectDetail:zoomMap')}
                                </button>}
                            {projectDetail.kmlSimplified && <Map project={projectDetail} />}
                        </div>}
                />
                <div className={styles.summary}>
                    <div className={styles.summaryThemes}>
                        {projectDetail.themes.map(theme => (
                            <ContentType key={theme.id} infrastructure={theme} className={styles.themeTag}
                                         textClass={styles.themeTagText} />
                        ))}
                    </div>
                    <span className={styles.summarySeparator} />
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{projectDetail.countries.length}</span>
                        <span className={styles.summaryLabel}>{i18next.t('projectDetail:countries')}</span>
                    </div>
                    <span className={styles.summarySeparator} />
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{projectDetail.territories.length}</span>
                        <span className={styles.summaryLabel}>{i18next.t('projectDetail:territories')}</span>
                    </div>
                    <span className={styles.summarySeparator} />
                    <div className={styles.summaryItem}>
                        <span className={`${styles.summaryLabel} ${styles.summaryLabelTwoLines}`}>
                            {i18next.t('projectDetail:dollarsInvested')}
                        </span>
                        <span className={`${styles.summaryValue} ${styles.summaryInvestment}`}>
                            {this.investment()}
                        </span>
                    </div>
                    <span className={styles.summarySeparator} />
                    <div className={`${styles.summaryItem} ${styles.summaryStage}`}>
                        <span className={styles.summaryLabel}>{i18next.t('technicalSheet:etapa')}</span>
                        <StageProgress order={projectDetail.stage.order} size="md" />
                    </div>
                </div>
                <div className={styles.body}>
                    <div className={styles.column}>
                        <DropDown title={i18next.t('technicalSheet:range')} open fill>
                            <div className={styles.dropdownContent}>
                                {this.renderDescription()}
                            </div>
                        </DropDown>
                    </div>
                    <div className={styles.column}>
                        <DropDown title={i18next.t('technicalSheet:general')} open>
                            <SingleContent
                                data={renderMany(COUNTRIES, names(projectDetail.countries), ' - ')}
                                valueClassName={styles.countries} />
                            <SingleContent data={renderMany(TERRITORIES, names(projectDetail.territories))} />
                            <SingleContent data={renderMany(AREA, names(projectDetail.scopes))} />
                            <SingleContent data={renderText(STAGE, projectDetail.stage.name)} />
                            <SingleContent
                                data={renderText(TERM, projectDetail.term ? projectDetail.term.name : '-')} />
                            <SingleContent data={renderMany(THEMES, names(projectDetail.themes))} />
                            <SingleContent data={renderMany(SUBTHEMES, names(projectDetail.subthemes))} />
                            <SingleContent data={renderMany(TYPES, names(projectDetail.types))} />
                            <SingleContent
                                data={renderText(SOURCE, projectDetail.source ? projectDetail.source.name : '-')} />
                        </DropDown>
                        {hasFundingInfo(projectDetail) &&
                            <DropDown title={i18next.t('technicalSheet:finance')}>
                                {this.renderFunding()}
                            </DropDown>}
                        <DropDown title={i18next.t('technicalSheet:responsibles')}>
                            {projectDetail.responsibles && projectDetail.responsibles.length ?
                                <ul className={styles.responsibles}>
                                    {projectDetail.responsibles.map(name => <li key={name}>{name}</li>)}
                                </ul> :
                                <p className={styles.responsibles}>-</p>}
                        </DropDown>
                    </div>
                </div>
                <ModalContent project={projectDetail} isOpen={modelOpen} closeModal={this.handleCloseModal} />
            </div>
        );
    }
}

export default withSpinner({ WrappedComponent: Layout });
